// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// Component imports:
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import DatePickerRange from '../common/DatePickerRange';
// Service imports:
import { formatDate, getDatePair } from '../../services/utils';
// Action imports:
import { requestCancelledTrades } from '../../redux-store/actions/reportActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import StickyHeaderTable from '../common/StickyHeaderTable';
import { exportToExcel } from './summaryPerClientOrTrader/helperFunctions';
import SortFilterTable from '../common/SortFilterTable';

//@ts-ignore
window.$.DataTable = require('datatables.net')

type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps;

interface IState {
    tableData: { 
        timestamp: string; 
        tradeID: string; 
        clientID: string; 
        side: string; 
        counterparty: string | null; 
        symbol: string; 
        cancelled: string; 
        cancelled_at: string; 
        actionTrader: string 
    }[];
}

class ReportOfCancelledTrades extends React.Component<Props, IState> {
    private dataTableInstance: any

    constructor(props: Props) {
        super(props);
        this.state = {
            tableData: [],
        };
        this.dataTableInstance = null;
    }

    isLoted = false;
    isError = false;

    componentDidMount() {
        this.initializeDataTable();
        this.fetchData();
    }
    componentDidUpdate(prevProps: Props) {
        if (this.props.fromDate !== prevProps.fromDate || this.props.toDate !== prevProps.toDate) {
            this.fetchData();
        }
        this.updateDataTable();
    }
    componentWillUnmount() {
        if (this.dataTableInstance) {
            this.dataTableInstance?.destroy();
        }
    }
    initializeDataTable = () => {
        // @ts-ignore
        this.dataTableInstance = window.$('#reportTable').DataTable({
            orderCellsTop: true,
            fixedHeader: false,
            pageLength: 100,
            scrollCollapse: true,
            scroller: true,
            deferRender: true,
            paging: false,
            info: false,
            searching: true,
            order: [[0, 'asc']],
            bLengthChange: false,
            dom: 'Bfrtip',
            buttons: ['copy', 'csv', 'excel', 'pdf', 'print']
        });
        $('.dt-button').removeClass('dt-button buttons-html5').addClass('btn-dark btn btn-sm');
    }

    // ===================================== Fetch Data From API =====================================
    fetchData = () => {
        const { fromDate, toDate, accessTokenV2, hasPrivateData, cancelledTradesMap, requestCancelledTrades } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const cancelledTradesInfo = cancelledTradesMap[datePair];
        if (cancelledTradesInfo === undefined && hasPrivateData) {
            requestCancelledTrades(fromDate, toDate, accessTokenV2);
        }
        this.isLoted = true;
    }

    // ===================================== Update Data Table Info =====================================
    updateDataTable = () => {
        const { cancelledTradesMap, fromDate, toDate } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const cancelledTradesInfo = cancelledTradesMap[datePair];
        // ----------------- If Error loading data -----------------
        if (cancelledTradesInfo instanceof Error) {
            this.isError = true;
        }
        // ----------------- If data is still loading -----------------
        else if (cancelledTradesInfo === 'waiting') {
            this.isLoted = true;
        }
        // ----------------- If data is loaded -----------------
        else if (this.isLoted && Array.isArray(cancelledTradesInfo)) {
            const tableData = cancelledTradesInfo.map((trade) => ({
                timestamp: formatDate(trade.timestamp),
                tradeID: trade.trade_id,
                clientID: Array.from(
                    new Set(
                        (trade.orders || [])
                            .map(order => order.record_keeping.client_id || '')
                            .filter(clientId => clientId) // This removes any empty strings
                    )
                ).join(', '),
                side: trade.side,
                counterparty: trade.counterparty,
                symbol: trade.symbol,
                cancelled: trade.cancelled,
                cancelled_at: formatDate(trade.cancelled_at),
                actionTrader: trade.orders.reduce((displayString, order) => (displayString + `${order.status}: ${order.trader_id}\n`), '')
            }));
            this.isLoted = false;
            this.setState({ tableData });
        }
    }

    render () {
        const { tableData } = this.state;

        return (
            <>
                <Row className='SecondaryNavBar'>
                    <Col xs={12} className='DatePickerRangeCol'>
                        <DatePickerRange />
                    </Col>
                </Row>
                <Row className='TableTitle'>
                    <Col xs={12} className='TableTitle-col'>
                        <h5 className='SectionTitle'>Cancelled standard trades</h5> 
                        <button 
                            id='excelButton' 
                            onClick={exportToExcel} 
                            className='VerySmallButton btn btn-dark btn-sm'
                        >Excel</button>
                    </Col>
                </Row>
                <Row className='TableRow'>
                    <Col xs={12} className='TableCol'>
                        <StickyHeaderTable hideScrollbar={false}>
                            <SortFilterTable
                                data={tableData}
                                isLoading={this.isLoted}
                                isError={this.isError}
                                rowId={({ timestamp }) => timestamp ?? undefined}
                                columns={[
                                    {
                                        title: 'Timestamp',
                                        textAlign: 'left',
                                        dataAccessor: ({ timestamp }) => timestamp,
                                        simpleSortable: ({ timestamp }) => timestamp,
                                        filter: {
                                            id: 'Timestamp',
                                            type: 'string',
                                            stringValue: ({ timestamp }) => timestamp
                                        }
                                    },
                                    {
                                        title: 'Trade ID',
                                        textAlign: 'left',
                                        dataAccessor: ({ tradeID }) => tradeID,
                                        simpleSortable: ({ tradeID }) => tradeID,
                                        filter: {
                                            id: 'Trade ID',
                                            type: 'string',
                                            stringValue: ({ tradeID }) => tradeID
                                        }
                                    },
                                    {
                                        title: 'Client IDs',
                                        textAlign: 'left',
                                        dataAccessor: ({ clientID }) => clientID,
                                        simpleSortable: ({ clientID }) => clientID,
                                        filter: {
                                            id: 'Client IDs',
                                            type: 'string',
                                            stringValue: ({ clientID }) => clientID
                                        }
                                    },
                                    {
                                        title: 'Side',
                                        textAlign: 'left',
                                        dataAccessor: ({ side }) => side,
                                        simpleSortable: ({ side }) => side,
                                    },
                                    {
                                        title: 'Counterparty',
                                        textAlign: 'left',
                                        dataAccessor: ({ counterparty }) => counterparty,
                                        simpleSortable: ({ counterparty }) => counterparty,
                                        filter: {
                                            id: 'Counterparty',
                                            type: 'string',
                                            stringValue: ({ counterparty }) => counterparty
                                        }
                                    },
                                    {
                                        title: 'Symbol',
                                        textAlign: 'left',
                                        dataAccessor: ({ symbol }) => symbol,
                                        simpleSortable: ({ symbol }) => symbol,
                                        filter: {
                                            id: 'Symbol',
                                            type: 'string',
                                            stringValue: ({ symbol }) => symbol
                                        }
                                    },
                                    {
                                        title: 'Cancelled',
                                        textAlign: 'left',
                                        dataAccessor: ({ cancelled }) => cancelled,
                                        simpleSortable: ({ cancelled }) => cancelled,
                                        filter: {
                                            id: 'Cancelled',
                                            type: 'string',
                                            stringValue: ({ cancelled }) => cancelled
                                        }
                                    },
                                    {
                                        title: 'Cancelled At',
                                        textAlign: 'left',
                                        dataAccessor: ({ cancelled_at }) => cancelled_at,
                                        simpleSortable: ({ cancelled_at }) => cancelled_at,
                                        filter: {
                                            id: 'Cancelled At',
                                            type: 'string',
                                            stringValue: ({ cancelled_at }) => cancelled_at
                                        }
                                    },
                                    {
                                        title: 'Action: Trader',
                                        textAlign: 'left',
                                        dataAccessor: ({ actionTrader }) => actionTrader,
                                        simpleSortable: ({ actionTrader }) => actionTrader,
                                    },
                                ]}
                            />
                        </StickyHeaderTable>
                    </Col>
                </Row>
            </>
        );
    }
}

function mapStateToProps({ dashboardReducer, reportReducer, loginReducer }: RootState) {
    const { dayRange } = dashboardReducer;
    const { fromDate, toDate } = dayRange;
    const { accessTokenV2, hasPrivateData } = loginReducer;
    const { cancelledTradesMap } = reportReducer;
    return {
        fromDate,
        toDate,
        accessTokenV2,
        hasPrivateData,
        cancelledTradesMap
    };
}

const connector = connect(mapStateToProps, { requestCancelledTrades });
export default connector(ReportOfCancelledTrades);
